import {Draft} from 'immer';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {WORKFLOWS_ANALYTICS_ACTIONS, ActionOrigin} from 'constants/AnalyticsConstants';

import {buildDataFlowEdge} from '../buildStaticDataFromFlowchartConfigGraphQL';
import {FlowchartStaticEdgeData, ProcessFlowchartEdgeType} from '../processFlowchartConfigEditorConstants';
import {
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
  TaskSchemaStaticNodeData,
} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartEdgeChange} from '../processFlowchartConfigEditorUtils';
import type {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {
  findNodePairsFulfillingTransition,
  findPlannedTransitionIdsForPTDVPair,
} from '../types/transition/transitionChangeUtils';
import {RelevantControlFlowEdge} from '../types/transition/transitionDataFlowEdgeSync';
import {parseFieldPairKey} from '../types/transition/transitionKeyUtils';
import type {FlowchartEdgeIndices} from '../types/transition/transitionLifecycleUtils';
import {
  buildFlowchartEdgeIndices,
  getFieldPairKeyFromDataFlowEdge,
  getPTDVIdsFromEdge,
  getTaskSchemaNodePairsForEdge,
} from '../types/transition/transitionLifecycleUtils';
import {Transition} from '../types/transition/TransitionTypes';
import {ApplyFlowchartEdgeChangesOptions} from './applyFlowchartEdgeChanges';

/**
 * Helpers for finding which node pairs fulfill which transitions, and vice versa
 */
function createTransitionFulfillmentHelpers({
  nodesStaticData,
  edgesAfterChanges,
  indicesAfterChanges,
  transitions,
}: {
  nodesStaticData: Array<StaticFlowchartNodeData>;
  edgesAfterChanges: Array<FlowchartStaticEdgeData>;
  indicesAfterChanges: FlowchartEdgeIndices;
  transitions: Record<string, Transition>;
}): {
  doesNodePairFulfillTransition: (
    transition: Transition,
    sourceNodeId: string,
    targetNodeId: string
  ) => boolean;
  getFulfilledTransitions: (
    sourceNode: TaskSchemaStaticNodeData,
    targetNode: TaskSchemaStaticNodeData
  ) => Array<Transition>;
  getPairsFulfillingTransition: (transition: Transition) => Array<RelevantControlFlowEdge>;
} {
  // Memoize the node pairs fulfilling each transition (computed against the post-batch edges).
  const fulfillingPairsByTransitionId = new Map<string, Array<RelevantControlFlowEdge>>();

  const getPairsFulfillingTransition = (transition: Transition): Array<RelevantControlFlowEdge> => {
    let pairs = fulfillingPairsByTransitionId.get(transition.id);
    if (pairs == null) {
      pairs = findNodePairsFulfillingTransition({
        nodes: nodesStaticData,
        edges: edgesAfterChanges,
        transition,
      });
      fulfillingPairsByTransitionId.set(transition.id, pairs);
    }
    return pairs;
  };

  const doesNodePairFulfillTransition = (
    transition: Transition,
    sourceNodeId: string,
    targetNodeId: string
  ): boolean =>
    getPairsFulfillingTransition(transition).some(
      (p) => p.sourceNodeId === sourceNodeId && p.targetNodeId === targetNodeId
    );

  const getFulfilledTransitions = (
    sourceNode: TaskSchemaStaticNodeData,
    targetNode: TaskSchemaStaticNodeData
  ): Array<Transition> =>
    findPlannedTransitionIdsForPTDVPair(
      sourceNode.taskDefinitionVersionSpec.clientId,
      targetNode.taskDefinitionVersionSpec.clientId,
      transitions
    )
      .map((id) => transitions[id])
      .filter(
        (t): t is Transition => t != null && doesNodePairFulfillTransition(t, sourceNode.id, targetNode.id)
      );

  return {doesNodePairFulfillTransition, getFulfilledTransitions, getPairsFulfillingTransition};
}

/**
 * Processes edge changes with mirroring, using transitions as the source of truth.
 *
 * Data flow (DF) edges are a *projection* of a transition's `customMappingDataByFieldPair` onto
 * every node pair that fulfills the transition (CF-connected, possibly through routers). Rather
 * than matching node pairs directly — which is ambiguous when overlapping transitions (e.g. a
 * single `A → C` coexisting with a merging `{A, B} → C`) share the same PTDV pair — this:
 *
 *   1. Builds an internal list of per-transition field-pair deltas from the incoming edge
 *      changes, keyed by transition ID (NOT by PTDV pair — a single PTDV pair can be covered by
 *      multiple transitions fulfilled by different node-pair sets).
 *   2. Projects each delta onto every node pair fulfilling *that* transition to generate the
 *      mirrored DF edge add/remove changes.
 *
 * The originating edge change is left untouched (it is applied directly); only the mirrored
 * sibling edges are generated here. Returns the incoming changes plus the generated ones.
 */
export function processEdgeChangesWithMirroring(
  edgeChanges: Array<FlowchartEdgeChange>,
  draft: Draft<FlowchartConfigWithMetadata>,
  options: ApplyFlowchartEdgeChangesOptions
): Array<FlowchartEdgeChange> {
  const {nodesStaticData, edgesStaticData} = draft.staticFlowchart;
  const transitions = draft.metadata.transitions;

  // Post-batch edge set: existing edges minus the ones being removed, plus the ones being added.
  // Fulfillment and CF resolution are computed against this so same-batch CF changes are seen.
  const edgeIdsBeingRemoved = new Set(
    edgeChanges.filter((change) => change.type === 'remove').map((change) => change.id)
  );
  const edgesAfterRemovals = edgesStaticData.filter((edge) => !edgeIdsBeingRemoved.has(edge.id));
  const addedEdges = edgeChanges.flatMap((change) =>
    change.type === 'add' && change.item != null ? [change.item as FlowchartStaticEdgeData] : []
  );
  const edgesAfterChanges = [...edgesAfterRemovals, ...addedEdges];
  const indicesAfterChanges = buildFlowchartEdgeIndices(nodesStaticData, edgesAfterChanges);

  const {getFulfilledTransitions, doesNodePairFulfillTransition, getPairsFulfillingTransition} =
    createTransitionFulfillmentHelpers({
      nodesStaticData,
      edgesAfterChanges,
      indicesAfterChanges,
      transitions,
    });

  // Phase 1: build per-transition field-pair deltas from the incoming edge changes.
  const addsByTransitionId: Partial<Record<string, Set<string>>> = {};
  const removesByTransitionId: Partial<Record<string, Set<string>>> = {};
  const recordDelta = (action: 'ADD' | 'REMOVE', transitionId: string, fieldPairKey: string): void => {
    const map = action === 'ADD' ? addsByTransitionId : removesByTransitionId;
    map[transitionId] ??= new Set<string>();
    map[transitionId].add(fieldPairKey);
    if (options.disallowTransitionEdits) {
      return;
    }
    if (action === 'REMOVE') {
      delete transitions[transitionId].customMappingDataByFieldPair[fieldPairKey];
    }
    if (
      action === 'ADD' &&
      !transitions[transitionId].customMappingDataByFieldPair.hasOwnProperty(fieldPairKey)
    ) {
      transitions[transitionId].customMappingDataByFieldPair[fieldPairKey] = null;
    }
  };

  for (const change of edgeChanges) {
    // Resolve the affected edge and the direction of the change
    const edge =
      change.type === 'add'
        ? (change.item as FlowchartStaticEdgeData | null)
        : change.type === 'remove'
          ? (edgesStaticData.find((e) => e.id === change.id) ?? null)
          : null;
    if (edge == null) {
      continue;
    }

    if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      // DF edges generate a transition change for each transition fulfilled by this edge
      const ptdvIds = getPTDVIdsFromEdge(edge, nodesStaticData);
      const fieldPairKey = getFieldPairKeyFromDataFlowEdge(edge, nodesStaticData);
      if (ptdvIds == null || fieldPairKey == null) {
        continue;
      }
      const sourceNode = indicesAfterChanges.nodeMap.get(edge.source);
      const targetNode = indicesAfterChanges.nodeMap.get(edge.target);
      assert(
        sourceNode != null &&
          targetNode != null &&
          isStaticTaskSchemaNodeData(sourceNode) &&
          isStaticTaskSchemaNodeData(targetNode),
        'Dataflow edge source/target nodes not found or not static task schema data'
      );
      const fulfilledTransitions = getFulfilledTransitions(sourceNode, targetNode);
      for (const transition of fulfilledTransitions) {
        recordDelta(change.type === 'add' ? 'ADD' : 'REMOVE', transition.id, fieldPairKey);
      }
    } else if (edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW && change.type === 'add') {
      // A new CF edge should receive the field pairs for the transitions it fulfills.
      // DF edges that already existed on that node pair get added to the transitions
      const resolvedPairs = getTaskSchemaNodePairsForEdge(
        edge,
        nodesStaticData,
        edgesAfterChanges,
        indicesAfterChanges
      );

      for (const {sourceNode: resolvedSource, targetNode: resolvedTarget} of resolvedPairs) {
        // Mirroring data flow edges for new control flow edges
        const existingDataFlowEdgesBetweenNodes = edgesAfterChanges.filter(
          (edge) =>
            edge.source === resolvedSource.id &&
            edge.target === resolvedTarget.id &&
            edge.type === ProcessFlowchartEdgeType.DATA_FLOW
        );
        const fieldPairKeysToMirror = new Set<string>();
        for (const dataFlowEdge of existingDataFlowEdgesBetweenNodes) {
          const fieldPairKey = getFieldPairKeyFromDataFlowEdge(dataFlowEdge, nodesStaticData);
          if (fieldPairKey != null) {
            fieldPairKeysToMirror.add(fieldPairKey);
          }
        }

        const fulfilledTransitions = getFulfilledTransitions(resolvedSource, resolvedTarget);
        for (const transition of fulfilledTransitions) {
          // Add existing field pairs from the newly connected source->target
          for (const fieldPairKey of fieldPairKeysToMirror) {
            recordDelta('ADD', transition.id, fieldPairKey);
          }
          // Add transition's field pairs onto the newly connected source->target
          for (const fieldPairKey of Object.keys(transition.customMappingDataByFieldPair)) {
            // This is not a true transition change, but we want to ensure that the new control flow
            // edge eventually gets the transition's DF edges. We could add the edges directly here,
            // but doing this unifies the logic
            recordDelta('ADD', transition.id, fieldPairKey);
          }
        }
      }
    }
  }

  // Phase 2: project per-transition deltas onto all node pairs fulfilling each transition.

  // Dedup adds against existing/just-added edges and against each other (overlapping transitions
  // share node pairs, so the same field pair can be reached via more than one transition).
  const existingEdgeKeys = new Set<string>();
  for (const edge of edgesAfterChanges) {
    if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      existingEdgeKeys.add(`${edge.sourceHandle}::${edge.targetHandle}`);
    }
  }

  const newEdgeChanges: Array<FlowchartEdgeChange> = [];
  if (Object.keys(removesByTransitionId).length > 0) {
    for (const edge of edgesAfterChanges) {
      if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
        continue;
      }
      // For DF edges that exist, we remove the ones that are specified by the transition deltas.
      // However, if the DF edge is needed by a different fulfilled transition on this node pair, it
      // should not be removed.
      const {source: sourceNodeId, target: targetNodeId} = edge;
      const sourceNode = indicesAfterChanges.nodeMap.get(sourceNodeId);
      const targetNode = indicesAfterChanges.nodeMap.get(targetNodeId);
      assert(sourceNode != null && targetNode != null, 'Fulfilling node pair not found in node map');
      assert(
        isStaticTaskSchemaNodeData(sourceNode) && isStaticTaskSchemaNodeData(targetNode),
        'Fulfilling node pair is not static task schema data'
      );
      const fieldPairKeyForEdge = getFieldPairKeyFromDataFlowEdge(edge, nodesStaticData);
      if (fieldPairKeyForEdge == null) {
        continue;
      }

      // Check if edge is slated for removal
      let shouldRemoveEdge = false;
      for (const transitionId of Object.keys(removesByTransitionId)) {
        const fieldPairKeys = removesByTransitionId[transitionId]!;
        if (doesNodePairFulfillTransition(transitions[transitionId], sourceNodeId, targetNodeId)) {
          if (fieldPairKeys.has(fieldPairKeyForEdge)) {
            shouldRemoveEdge = true;
            break;
          }
        }
      }
      if (!shouldRemoveEdge) {
        continue;
      }

      const fulfilledTransitions = getFulfilledTransitions(sourceNode, targetNode);
      // Check if edge is needed for any other transition on this node pair
      for (const transition of fulfilledTransitions) {
        if (transition.customMappingDataByFieldPair.hasOwnProperty(fieldPairKeyForEdge)) {
          // This transition still needs this edge, so it should not be removed.
          shouldRemoveEdge = false;
          break;
        }
      }
      if (shouldRemoveEdge) {
        newEdgeChanges.push({
          type: 'remove',
          id: edge.id,
        });
      }
    }
  }

  for (const transitionId of Object.keys(addsByTransitionId)) {
    const fieldPairKeys = addsByTransitionId[transitionId]!;
    const transition = transitions[transitionId];
    if (transition == null) {
      continue;
    }
    const fulfillingPairs = getPairsFulfillingTransition(transition);
    for (const fieldPairKey of fieldPairKeys) {
      const parsed = parseFieldPairKey(fieldPairKey);
      if (parsed == null) {
        continue;
      }
      const ptdvPairKey = `${parsed.sourcePTDVId}::${parsed.targetPTDVId}`;
      for (const pair of fulfillingPairs) {
        if (pair.ptdvPairKey !== ptdvPairKey) {
          continue;
        }
        const edge = buildDataFlowEdge({
          sourceNodeId: pair.sourceNodeId,
          targetNodeId: pair.targetNodeId,
          fromFieldId: parsed.sourceFieldClientId,
          fromFieldSectionType: parsed.sourceSectionType,
          toFieldId: parsed.targetFieldClientId,
          toFieldSectionType: parsed.targetSectionType,
        });
        const edgeKey = `${edge.sourceHandle}::${edge.targetHandle}`;
        if (existingEdgeKeys.has(edgeKey)) {
          continue;
        }
        existingEdgeKeys.add(edgeKey);
        newEdgeChanges.push({type: 'add', item: edge});
      }
    }
  }

  const numEdgesAdded = newEdgeChanges.filter((change) => change.type === 'add').length;
  const numEdgesRemoved = newEdgeChanges.filter((change) => change.type === 'remove').length;
  const analyticsMetadata = {
    actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
    flowchartType: draft.metadata.flowchartTypeName,
    editorMode: options.editorMode,
  };
  if (numEdgesAdded > 0) {
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.DATA_FLOW_EDGE_ADDED_VIA_MIRRORING, {
      ...analyticsMetadata,
      numEdgesMirrored: numEdgesAdded,
    });
  }
  if (numEdgesRemoved > 0) {
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.DATA_FLOW_EDGE_REMOVED_VIA_MIRRORING, {
      ...analyticsMetadata,
      numEdgesMirrored: numEdgesRemoved,
    });
  }

  return [...edgeChanges, ...newEdgeChanges];
}
